<template>
  <v-menu
    v-model="open"
    :close-on-content-click="true"
    :open-on-click="false"
    offset-y
    nudge-bottom="6"
    max-height="460"
    min-width="440">
    <template #activator="{ attrs }">
      <label
        class="global-search"
        v-bind="attrs">
        <v-icon size="18">mdi-magnify</v-icon>
        <input
          ref="input"
          v-model="query"
          placeholder="Rechercher un compte, un utilisateur, un programme"
          @focus="open = query.length >= 2"
          @keydown.esc="close"
          @keydown.enter="openFirst">
        <kbd>⌘ K</kbd>
      </label>
    </template>
    <div class="global-search__results">
      <div
        v-if="loading && !hasResults"
        class="global-search__empty">
        Recherche…
      </div>
      <div
        v-else-if="!hasResults"
        class="global-search__empty">
        Aucun résultat pour « {{ query }} »
      </div>
      <template v-for="group in groups">
        <div
          v-if="group.items.length"
          :key="group.key">
          <div class="global-search__group">
            {{ group.label }}
          </div>
          <button
            v-for="item in group.items"
            :key="`${group.key}-${item.pk}`"
            class="global-search__item"
            @click="go(group.to(item))">
            <v-icon size="18">
              {{ group.icon }}
            </v-icon>
            <span class="global-search__name">{{ group.title(item) }}</span>
            <span class="global-search__sub">{{ group.sub(item) }}</span>
          </button>
        </div>
      </template>
    </div>
  </v-menu>
</template>

<script>
  import { debounce } from 'lodash'
  import RESOURCES from '@batimentb/vadimm-js-api/resources'

  // Recherche rapide (⌘K) dans les comptes, utilisateurs et programmes
  const SOURCES = [
    {
      key: 'accounts',
      label: 'Comptes',
      icon: 'mdi-briefcase-outline',
      resource: RESOURCES.ACCOUNT,
      title: a => a.name,
      sub: a => (a.account_type === 'promoteur' ? 'Promoteur' : 'Commercialisateur'),
      to: a => ({ name: 'accountVue', params: { id: a.pk } })
    },
    {
      key: 'users',
      label: 'Utilisateurs',
      icon: 'mdi-account-outline',
      resource: RESOURCES.USER,
      title: u => `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email,
      sub: u => u.email,
      to: u => ({ name: 'users', query: { searchable_name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email } })
    },
    {
      key: 'programs',
      label: 'Programmes',
      icon: 'mdi-office-building',
      resource: RESOURCES.PROGRAM,
      title: p => p.name,
      sub: p => (p.promoteur && p.promoteur.name) || '',
      to: p => ({ name: 'programs', query: { searchable_name: p.name } })
    }
  ]

  export default {
    name: 'GlobalSearch',
    data () {
      return {
        query: '',
        open: false,
        loading: false,
        requestId: 0,
        groups: SOURCES.map(s => ({ ...s, items: [] }))
      }
    },
    computed: {
      hasResults () {
        return this.groups.some(g => g.items.length)
      }
    },
    watch: {
      query (val) {
        if (val.trim().length < 2) {
          this.open = false
          return
        }
        this.loading = true
        this.open = true
        this.search()
      }
    },
    created () {
      this.search = debounce(this.fetch, 250)
    },
    mounted () {
      window.addEventListener('keydown', this.onShortcut)
    },
    beforeDestroy () {
      window.removeEventListener('keydown', this.onShortcut)
    },
    methods: {
      onShortcut (event) {
        if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
          event.preventDefault()
          this.$refs.input.focus()
          this.$refs.input.select()
        }
      },
      async fetch () {
        const requestId = ++this.requestId
        const term = this.query.trim()
        const results = await Promise.all(this.groups.map(group =>
          this.$api.axios.get(group.resource, { params: { searchable_name: term, per_page: 5 } })
            .then(({ data }) => data.items || [])
            .catch(() => [])
        ))
        if (requestId !== this.requestId) return
        this.groups.forEach((group, i) => { group.items = results[i] })
        this.loading = false
      },
      openFirst () {
        const group = this.groups.find(g => g.items.length)
        if (group) this.go(group.to(group.items[0]))
      },
      go (to) {
        this.close()
        this.$router.push(to).catch(() => {})
      },
      close () {
        this.open = false
        this.query = ''
        this.$refs.input.blur()
      }
    }
  }
</script>

<style scoped>
  .global-search {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    max-width: 460px;
    height: 40px;
    padding: 0 10px 0 14px;
    border-radius: 12px;
    background: #f4f4f5;
    cursor: text;
  }
  .global-search:focus-within {
    background: white;
    box-shadow: 0 0 0 1.5px #111113;
  }
  .global-search .v-icon {
    color: #7a7a85;
  }
  .global-search input {
    flex: 1;
    min-width: 0;
    border: 0;
    outline: 0;
    background: transparent;
    font-size: 13.5px;
    color: #111113;
  }
  .global-search input::placeholder {
    color: #8b8b95;
  }
  .global-search kbd {
    font-family: inherit;
    font-size: 11px;
    font-weight: 600;
    color: #7a7a85;
    background: white;
    border: 1px solid #ececef;
    border-radius: 6px;
    padding: 1px 6px;
    box-shadow: none;
  }
  .global-search__results {
    background: white;
    padding: 6px;
  }
  .global-search__group {
    padding: 10px 10px 4px;
    font-size: 11.5px;
    font-weight: 600;
    color: #a1a1aa;
  }
  .global-search__item {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 8px 10px;
    border-radius: 8px;
    text-align: left;
    font-size: 13.5px;
  }
  .global-search__item:hover {
    background: #f4f4f5;
  }
  .global-search__name {
    font-weight: 600;
    white-space: nowrap;
  }
  .global-search__sub {
    margin-left: auto;
    padding-left: 12px;
    color: #7a7a85;
    font-size: 12.5px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .global-search__empty {
    padding: 14px 12px;
    color: #7a7a85;
    font-size: 13px;
  }
</style>
